import GoogleIcon from '@mui/icons-material/Google';
import { Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/constants/routes';
import { GoogleConsentDialog } from '@/features/identity/components/GoogleConsentDialog';
import {
  GoogleSignInCancelledError,
  useGoogleOAuth,
} from '@/features/identity/hooks/useGoogleOAuth';
import type { MfaChallengeState } from '@/features/identity/types/identity.types';
import { useToast } from '@/hooks/useToast';
import type { LoginResult } from '@/types/auth.types';
import { parseRestError } from '@/utils/errorUtils';

/** The backend's signal that this Google sign-in would create an account and needs consent first. */
const ACCEPTANCE_REQUIRED_CODE = 'Legal.AcceptanceRequired';

export interface SocialLoginButtonsProps {
  /** Called once the provider successfully establishes a session, so the page can navigate on. */
  onSuccess: () => void;
  /**
   * Called the moment a provider flow starts, before the popup opens, so the host form can drop
   * errors it is already showing — nothing about the abandoned password path should be left on
   * screen behind the popup. Errors a *blur* would raise are prevented in the button instead (see
   * `onMouseDown`); they cannot be cleared here, because that validation resolves after this runs.
   */
  onStart?: () => void;
}

/**
 * Social sign-in. Google is the only provider offered: Google Identity Services' token-client popup
 * is safe for a pure SPA, and the button is disabled with a tooltip when `VITE_GOOGLE_CLIENT_ID` is
 * unset for the environment.
 *
 * Additional providers are a deliberate product decision, not a technical limit — the backend keeps
 * a provider-agnostic OAuth pipeline (`IOAuthProvider`, `POST /auth/{provider}`) ready for one to be
 * surfaced here.
 */
export function SocialLoginButtons({ onSuccess, onStart }: SocialLoginButtonsProps) {
  const { loginWithGoogle } = useAuth();
  const { requestAccessToken, isConfigured: googleConfigured } = useGoogleOAuth();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  // Set only when the backend refused a first-time registration pending consent (ADR-0026) — holds the
  // SAME access token so confirming retries the exchange rather than reopening the Google popup.
  const [pendingConsentToken, setPendingConsentToken] = useState<string | null>(null);

  const afterLogin = (result: LoginResult, accessToken: string) => {
    // MFA-required (BR-2): hand off to the MFA page identically to the password path, carrying the
    // provider token to replay with the code (and the pending secret to scan, if not yet enrolled).
    if (result.mfaRequired) {
      const state: MfaChallengeState = {
        method: 'google',
        accessToken,
        enrollment: result.mfaSetup ?? undefined,
      };
      navigate(ROUTES.mfaChallenge, { state });
      return;
    }
    onSuccess();
  };

  const handleGoogle = async () => {
    onStart?.();
    setIsGoogleLoading(true);
    // Declared outside the try block on purpose: the catch clause needs the token from Google to hold
    // onto it for the consent retry, and a `const` scoped to `try` is invisible inside `catch`.
    let accessToken: string | undefined;
    try {
      accessToken = await requestAccessToken();
      const result = await loginWithGoogle(accessToken);
      afterLogin(result, accessToken);
    } catch (error) {
      // Dismissing the popup is a deliberate choice, not a failure — reset quietly, no toast.
      if (error instanceof GoogleSignInCancelledError) {
        // no-op
      } else if (accessToken && parseRestError(error).code === ACCEPTANCE_REQUIRED_CODE) {
        // This exchange would create an account; show the consent step rather than a generic error.
        // The token we already obtained from Google is reused on confirm — Google is not asked again.
        setPendingConsentToken(accessToken);
      } else {
        showError(parseRestError(error).message);
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleConsentConfirm = async () => {
    if (!pendingConsentToken) return;
    setIsGoogleLoading(true);
    try {
      const result = await loginWithGoogle(pendingConsentToken, undefined, true);
      setPendingConsentToken(null);
      afterLogin(result, pendingConsentToken);
    } catch (error) {
      setPendingConsentToken(null);
      showError(parseRestError(error).message);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Divider>
        <Typography variant="caption" color="text.secondary">
          or continue with
        </Typography>
      </Divider>
      {/* Single provider: the row wrapper that split the width between two buttons is gone, so
          Google spans the form width rather than leaving a dead half-column behind. */}
      <Tooltip title={googleConfigured ? '' : 'Google sign-in is not configured in this environment'}>
        <span>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            disabled={!googleConfigured || isGoogleLoading}
            // Keeps focus where it is instead of moving it to this button. A host form that
            // validates `onBlur` would otherwise validate the field being left — an empty Email
            // paints "Enter a valid email address" behind the Google popup, about a form the user
            // has just abandoned. Clearing the error afterwards does NOT fix this: RHF's blur
            // validation is async and resolves after the click handler, so it simply reinstates it.
            // Not preventing the blur is the only order that works.
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleGoogle}
          >
            Google
          </Button>
        </span>
      </Tooltip>
      <GoogleConsentDialog
        open={pendingConsentToken !== null}
        loading={isGoogleLoading}
        onConfirm={handleConsentConfirm}
        onCancel={() => setPendingConsentToken(null)}
      />
    </Stack>
  );
}
